import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Chip,
  Autocomplete,
  ImageList,
  ImageListItem,
  ImageListItemBar,
} from '@mui/material';
import {
  MyLocation,
  Save,
  Refresh,
  CloudUpload,
  Delete,
  Image as ImageIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import api from '../../utils/api';
import { INDIAN_CITIES } from '../../utils/indianCities';
import { GRADIENTS, RADIUS_LIBRARIAN as RADIUS } from '../../theme/tokens';

// Fix for default marker icon in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Available amenities options
const AMENITIES_OPTIONS = [
  'WiFi',
  'Air Conditioning',
  'Parking',
  'Cafeteria',
  'Water Dispenser',
  'Locker Facility',
  'Power Backup',
  'CCTV',
  '24/7 Access',
  'Study Rooms',
  'Discussion Rooms',
  'Printing Services',
  'Library',
  'Whiteboard',
  'Bean Bags',
  'Ergonomic Chairs',
  'Natural Light',
  'Silent Zone',
  'Charging Points',
  'Stationery Store',
  'Private Cabin',
  'Open Cabin',
  'Books',
  'Competitive Exam Books',
  'Magazines',
  'Children Books',
  'Co-Working',
];

// Component to handle map clicks and marker dragging
function LocationMarker({ position, setPosition }) {
  const markerRef = useRef(null);

  const map = useMapEvents({
    click(e) {
      setPosition([e.latlng.lat, e.latlng.lng]);
    },
  });

  const eventHandlers = {
    dragend() {
      const marker = markerRef.current;
      if (marker != null) {
        const newPos = marker.getLatLng();
        setPosition([newPos.lat, newPos.lng]);
      }
    },
  };

  useEffect(() => {
    if (position && map) {
      map.flyTo(position, map.getZoom());
    }
  }, [position, map]);

  return position === null ? null : (
    <Marker
      position={position}
      draggable={true}
      eventHandlers={eventHandlers}
      ref={markerRef}
    >
      <Popup>
        Your library is here. <br />
        Drag the marker to adjust location.
      </Popup>
    </Marker>
  );
}

function LibrarySettings({ onBack }) {
  const [library, setLibrary] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    locality: '',
    capacity: '',
    lat: '',
    lng: '',
    amenities: [],
  });
  const [profileData, setProfileData] = useState({
    phone: '',
  });
  const [position, setPosition] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [savingProfile, setSavingProfile] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchLibrary();
    fetchProfile();
  }, []);

  const fetchLibrary = async () => {
    try {
      setLoading(true);
      const response = await api.get('/librarian/library');
      const lib = response.data.data.library;
      setLibrary(lib);
      setFormData({
        name: lib.name || '',
        address: lib.address || '',
        city: lib.city || '',
        locality: lib.locality || '',
        capacity: lib.capacity || '',
        lat: lib.lat || '',
        lng: lib.lng || '',
        amenities: lib.amenities || [],
      });
      setGallery(lib.gallery || []);

      // Set map position if coordinates exist
      if (lib.lat && lib.lng) {
        setPosition([parseFloat(lib.lat), parseFloat(lib.lng)]);
      } else {
        // Default to India center if no coordinates
        setPosition([20.5937, 78.9629]);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch library details');
    } finally {
      setLoading(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await api.get('/librarian/profile');
      const profile = response.data.data.profile;
      setProfileData({
        phone: profile.phone || '',
      });
    } catch (err) {
      console.error('Failed to fetch profile:', err);
    }
  };

  const handleProfileChange = (e) => {
    setProfileData({ ...profileData, [e.target.name]: e.target.value });
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    setError('');
    try {
      await api.put('/librarian/profile', profileData);
      setSuccess('Phone number updated successfully!');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update phone number');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAmenitiesChange = (event, newValue) => {
    setFormData({ ...formData, amenities: newValue });
  };

  const detectLocation = () => {
    setDetectingLocation(true);
    setError('');

    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      setDetectingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setPosition([lat, lng]);
        setFormData({
          ...formData,
          lat: lat.toString(),
          lng: lng.toString(),
        });
        setSuccess('Location detected successfully!');
        setDetectingLocation(false);
      },
      (error) => {
        setError('Failed to detect location: ' + error.message);
        setDetectingLocation(false);
      }
    );
  };

  // Update form data when position changes from map
  useEffect(() => {
    if (position) {
      setFormData((prev) => ({
        ...prev,
        lat: position[0].toFixed(6),
        lng: position[1].toFixed(6),
      }));
    }
  }, [position]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const submitData = {
        name: formData.name,
        address: formData.address,
        city: formData.city || null,
        locality: formData.locality || null,
        capacity: parseInt(formData.capacity, 10),
        lat: formData.lat ? parseFloat(formData.lat) : null,
        lng: formData.lng ? parseFloat(formData.lng) : null,
        amenities: formData.amenities,
      };

      await api.put('/librarian/library', submitData);
      setSuccess('Library details updated successfully!');
      fetchLibrary();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update library details');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingImages(true);
    setError('');

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('images', files[i]);
    }

    try {
      const response = await api.post('/librarian/library/images', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setGallery(response.data.data.gallery);
      setSuccess(`${files.length} image(s) uploaded successfully!`);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload images');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleDeleteImage = async (imageUrl) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      const response = await api.delete('/librarian/library/images', {
        data: { imageUrl },
      });
      setGallery(response.data.data.gallery);
      setSuccess('Image deleted successfully!');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete image');
    }
  };

  if (loading && !library) {
    return (
      <Box p={3}>
        <Typography>Loading library details...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h5" fontWeight="700" gutterBottom>
          Library Settings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage your library details, images, amenities and location
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Library Details */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Basic Information
              </Typography>

              <Box mt={2}>
                <TextField
                  fullWidth
                  label="Library Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  margin="normal"
                />

                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  multiline
                  rows={3}
                  margin="normal"
                />

                <Autocomplete
                  options={INDIAN_CITIES}
                  value={formData.city || null}
                  onChange={(event, newValue) => {
                    setFormData({ ...formData, city: newValue || '' });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="City"
                      margin="normal"
                      placeholder="Search for a city..."
                    />
                  )}
                  freeSolo
                  autoHighlight
                />

                <TextField
                  fullWidth
                  label="Locality / Area"
                  name="locality"
                  value={formData.locality}
                  onChange={handleChange}
                  margin="normal"
                  placeholder="e.g. Uttam Nagar"
                  helperText="e.g. Uttam Nagar — used for area-based discovery"
                  inputProps={{ maxLength: 100 }}
                />

                <TextField
                  fullWidth
                  label="Capacity (Number of Seats)"
                  name="capacity"
                  type="number"
                  value={formData.capacity}
                  onChange={handleChange}
                  required
                  inputProps={{ min: 1 }}
                  margin="normal"
                />
              </Box>
            </Paper>
          </Grid>

          {/* Librarian Contact - WhatsApp Notifications */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Your Contact Number
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                This phone number will be used for WhatsApp notifications when you receive new bookings
              </Typography>

              <TextField
                fullWidth
                label="WhatsApp Phone Number"
                name="phone"
                value={profileData.phone}
                onChange={handleProfileChange}
                placeholder="+91 9876543210"
                helperText="Include country code (e.g., +91 for India)"
                margin="normal"
              />

              <Button
                variant="contained"
                onClick={handleSaveProfile}
                disabled={savingProfile}
                sx={{
                  mt: 2,
                  background: GRADIENTS.brand,
                }}
              >
                {savingProfile ? 'Saving...' : 'Save Phone Number'}
              </Button>
            </Paper>
          </Grid>

          {/* Amenities */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Amenities
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Select all amenities available at your library
              </Typography>

              <Autocomplete
                multiple
                options={AMENITIES_OPTIONS}
                value={formData.amenities}
                onChange={handleAmenitiesChange}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                      color="primary"
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Amenities"
                    placeholder="Choose amenities"
                  />
                )}
              />

              {formData.amenities.length > 0 && (
                <Box mt={2}>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Selected: {formData.amenities.length} amenities
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Library Gallery */}
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                  <Typography variant="h6" fontWeight="600">
                    Library Gallery
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Upload photos of your library (Max 10 images, 5MB each)
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<CloudUpload />}
                  disabled={uploadingImages}
                  sx={{
                    background: GRADIENTS.brand,
                  }}
                >
                  {uploadingImages ? 'Uploading...' : 'Upload Images'}
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </Button>
              </Box>

              {gallery.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <ImageIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    No images uploaded yet
                  </Typography>
                </Box>
              ) : (
                <ImageList cols={4} gap={12}>
                  {gallery.map((imageUrl, index) => (
                    <ImageListItem key={index}>
                      <img
                        src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${imageUrl}`}
                        alt={`Library ${index + 1}`}
                        loading="lazy"
                        style={{ height: 200, objectFit: 'cover', borderRadius: 4 }}
                      />
                      <ImageListItemBar
                        actionIcon={
                          <IconButton
                            sx={{ color: 'rgba(255, 255, 255, 0.9)' }}
                            onClick={() => handleDeleteImage(imageUrl)}
                          >
                            <Delete />
                          </IconButton>
                        }
                      />
                    </ImageListItem>
                  ))}
                </ImageList>
              )}
            </Paper>
          </Grid>

          {/* Location Coordinates */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="600">
                  Location Coordinates
                </Typography>
                <Tooltip title="Detect my location">
                  <IconButton
                    color="primary"
                    onClick={detectLocation}
                    disabled={detectingLocation}
                  >
                    <MyLocation />
                  </IconButton>
                </Tooltip>
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                Click on the map or drag the marker to set your library's location
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Latitude"
                    name="lat"
                    value={formData.lat}
                    onChange={(e) => {
                      handleChange(e);
                      const lat = parseFloat(e.target.value);
                      if (!isNaN(lat) && formData.lng) {
                        setPosition([lat, parseFloat(formData.lng)]);
                      }
                    }}
                    type="number"
                    inputProps={{ step: 0.000001 }}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Longitude"
                    name="lng"
                    value={formData.lng}
                    onChange={(e) => {
                      handleChange(e);
                      const lng = parseFloat(e.target.value);
                      if (!isNaN(lng) && formData.lat) {
                        setPosition([parseFloat(formData.lat), lng]);
                      }
                    }}
                    type="number"
                    inputProps={{ step: 0.000001 }}
                    margin="normal"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Map */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} data-aurora-opaque="true" sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Map View
              </Typography>
              <Box sx={{ height: 300, borderRadius: RADIUS.md, overflow: 'hidden' }}>
                {position && (
                  <MapContainer
                    center={position}
                    zoom={13}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <LocationMarker position={position} setPosition={setPosition} />
                  </MapContainer>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Submit Button */}
          <Grid item xs={12}>
            <Box display="flex" gap={2}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                startIcon={<Save />}
                disabled={loading}
                sx={{
                  background: GRADIENTS.brand,
                  px: 4,
                }}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<Refresh />}
                onClick={fetchLibrary}
                disabled={loading}
              >
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}

export default LibrarySettings;
